/**
 * Every user-visible string in the client, in one file, one exported const
 * per screen, so the Task 8 copy pass edits this file only. Plain, warm,
 * specific; sentence case; no exclamation marks; no em dashes; no ellipsis
 * characters anywhere (repo-wide rule). Nurses never see a price, so no
 * copy here ever names one.
 */
// ⭐ T1723 M7 — the ONE import in this copy module, and it is type-only: it adds no
// runtime edge, it exists so the request-status maps below are exhaustive.
import type { RequestStatus, EquipmentFormOption } from '../shared/request-schema.js';


export const sharedCopy = {
  appName: 'ECMB Taxi',
  loading: 'Loading',
  tryAgain: 'Try again',
  networkError: 'Could not reach the server. Check the connection and try again.',
  genericError: 'Something did not work. Try again.',
};

export const enrolCopy = {
  title: 'Sign in',
  emailLabel: 'Work email',
  emailPlaceholder: 'you@homename.example',
  sendCodeButton: 'Send me a code',
  codeSentNotice: 'If that email is set up here, a code is on its way. Check the inbox and enter it below.',
  codeLabel: '6-digit code',
  codePlaceholder: '000000',
  verifyButton: 'Sign in',
  useDifferentEmailLink: 'Use a different email',
  resendCodeLink: 'Send the code again',
  invalidCodeError: 'That code did not work. Check it and try again.',
  /**
   * Shown on Enrol when the nurse arrives from Picker's "trouble with your PIN"
   * route. ⛔ CONDITIONAL ON PURPOSE. It replaced a `pinResetNotice` that stated
   * flatly "that PIN was reset" — which the client cannot know post-786, because
   * pin-login answers every failure identically. It also must not promise she can
   * set a new PIN: `pin-set` 403s unless the session is pin-pending
   * (server/auth-routes.ts), and that is set only when `pin_hash` is null at
   * code-verify. A nurse who merely mistyped signs in, lands `active`, and is
   * offered no PIN screen at all. Both branches of the sentence below are true
   * without asserting which one she is in.
   */
  pinTroubleNotice: 'Sign in with your work email. If your PIN was cleared, you will be asked to choose a new one.',
  wrongHomeDeviceError: 'This device is set up for a different home. Ask a manager before signing in here.',
  // Demo-only roster (GET /api/dev/nurses). Absent in real mode, where the
  // enumeration hardening means a wrong address must stay indistinguishable
  // from a right one.
  demoRosterTitle: 'Demo sign-in',
  demoRosterHint: 'Testing only. These are the seeded nurses. Tap one to fill in their email.',
};

export const pinSetupCopy = {
  title: 'Set up a PIN',
  intro: 'Choose a PIN of 6 to 10 digits. Use it to unlock this device quickly next time.',
  pinLabel: 'New PIN',
  confirmLabel: 'Enter it again',
  mismatchError: 'Those two PINs do not match.',
  invalidError: 'Use 6 to 10 digits.',
  weakError: 'Pick a code that is not one repeated digit or a run like 123456.',
  saveButton: 'Save PIN',
};

/**
 * Mirrors server/auth-throttle.ts DEVICE_PIN_WINDOW_MS. The client bundle must
 * not import server code, so this is hand-mirrored; test/client/enter-submit.spec.tsx
 * pins the two together so a change to the server window reds here instead of
 * drifting silently.
 *
 * ⚠️ That spec imports the SERVER module. Nothing enforces the separation but
 * placement: the import must stay in the spec and must never appear anywhere
 * under src/client.
 *
 * ⚠️ This is an UPPER BOUND on the wait, not the remaining wait. The device
 * window is fixed from the FIRST failure and a refusal never extends it, so the
 * real pause is at most this long. Copy may overstate the wait, never
 * understate it, and it routes her to email either way.
 */
export const DEVICE_PIN_PAUSE_MINUTES = 15;

export const pickerCopy = {
  fallbackTitle: 'Who is this?',
  subtitle: 'Tap your name',
  differentNurseLink: 'Different nurse? Sign in with email code',
  pinPromptTitle: 'Enter your PIN',
  pinBackLink: 'Not you? Go back',
  pinLabel: 'PIN',
  unlockButton: 'Unlock',
  /** ⭐ Board 2706 rework (R3): shown on the lock screen while a New request is held for whoever
   *  signs back in (any sign-in route, same account). Names nothing about the request. */
  requestKept: 'Your request is kept. Sign back in to carry on.',
  /**
   * ⛔ ONE STRING FOR EVERY 401 — board 786. pin-login answers "wrong PIN",
   * "this nurse has no PIN" and "that was the 5th, her PIN is now destroyed"
   * with an identical body, so the client must not try to tell them apart. It
   * therefore names the remedy that works in every case instead of describing
   * her state, and promises nothing it cannot see (no "your PIN was reset", no
   * "set a new PIN" — see enrolCopy.pinTroubleNotice).
   */
  pinFailedError: 'That PIN did not work. Try again, or sign in with your work email instead.',
  /**
   * The per-device walk cap's 429 (board 787). Email sign-in is deliberately NOT
   * gated by that cap, which is what makes this instruction truthful rather than
   * a platitude.
   */
  deviceCappedError:
    `Too many PIN attempts on this device. PIN unlock is paused for about ${String(DEVICE_PIN_PAUSE_MINUTES)} minutes. ` +
    'Email sign-in still works, so use your work email instead.',
  /** Always visible on the PIN pad: the recovery route a de-credentialed nurse
   *  can no longer be routed to automatically, so she must be able to find it. */
  useEmailLink: 'Sign in with email', // T2173: a question read as help, not as the next step; PICKER_EMAIL_HINT names the case. VALUE-ONLY, one line
};

export const shellCopy = {
  // The banner landmark's accessible name (ADR4-D8, T1033 M6). Names the
  // ORGANISATION, not the screen — every portal surface now carries the same
  // brand bar, so a per-screen name here would be wrong on five of them.
  brandLabel: 'East Coast Minibus Hire',
  lockButton: 'Lock',
  tabNewRequest: 'New request',
  tabMyRequests: 'My requests',
  /** ⭐ T1717 M3. ⛔ NOT "Residents" AND NOT "My resident list": the first is a
   *  bare noun beside two possessive siblings, and the second is longer than
   *  the tab strip can hold at 320px. It also must not contain the e2e portal
   *  lane's `getByLabel`/`getByRole` needles as a substring (DD11) - "My
   *  requests" is the near miss and this differs from it in the last word. */
  tabMyResidents: 'My residents',
  tabRequests: 'All requests', // T2173: was 'Requests', ambiguous beside 'My requests' (Alex, 2026-09-18). VALUE-ONLY EDIT, one line — the four check:copy keys sit below
  tabStaff: 'Staff', // T2075 M5 renders it; declared here so the strip's words live together
  idleNudge: 'Still there? Tap or click anywhere to stay signed in, or it locks in a minute.', // T2075 M6: names the auto-lock; board 2706: a desk PC has no touch screen. ⛔ ONE LINE: the four check:copy keys sit below
};

export const requestFormCopy = {
  title: 'New request',
  residentLabel: 'Resident',
  residentPlaceholder: 'Type their name',
  // One resident field (2026-07-21 review, P8): typing a known name selects
  // them; typing a new name adds them on send. No separate add panel.
  //
  // ⛔ THE `*Real` VARIANTS ARE NOT DECORATION (T1131). `POST /api/residents`
  // is the Article 9 free-text PII surface and 403s outside synthetic mode, so
  // the synthetic strings below PROMISE something a real-mode server refuses.
  // Which pair renders is decided by the server's `residentsWritable` flag from
  // GET /api/meta, never inferred in the browser.
  newResidentHint: (name: string): string =>
    `New resident: ${name} will be added to your home's list when you send.`,
  /**
   * ⭐⭐ THE REFUSAL IS NOW HONEST ABOUT WHOSE ACT IT IS (T1717 M3, P1-1).
   *
   * ⛔ IT USED TO END "or ring East Coast Minibus to add them", which was
   * WRONG IN BOTH HALVES the moment the roster tab existed. Ringing the office
   * adds nobody: `POST /api/residents` is the only writer, and the office has
   * no screen that calls it. And the sentence blamed the nurse's own list when
   * the actual cause is a capability that has not been switched on yet - the
   * `PORTAL_RESIDENT_WRITES_ACK_ARTICLE9` acknowledgement, which is Alex's to
   * set (plan DD1). ⛔ SO NO SENTENCE IN THIS FILE MAY OFFER HELEN OR THE PHONE
   * AS THE REMEDY FOR A RESIDENT AGAIN - it sends a nurse on a ward to a phone
   * call that cannot help her.
   *
   * ⚠️ "not switched on HERE yet" is deliberate: the same build is the demo and
   * the production one, and the nurse cannot be told which she is looking at.
   * "Here" is true in both, and "yet" is true because the opening is a decision
   * somebody takes rather than a limitation of the product.
   */
  newResidentHintReal: (name: string): string =>
    `${name} is not on your home's list. Adding residents is not switched on here yet.`,
  // ⚠️ SUPPRESSED rather than re-worded in real mode, deliberately: "It is
  // saved to your resident list too" has no true real-mode variant, and
  // inventing one would state something the server will refuse.
  roomNewResidentHint: 'Their room. It is saved to your resident list too.',
  // ⛔ THE FIRST SENTENCE IS BYTE-PINNED by test/client/add-resident.spec.tsx
  // and is the true half. The second half used to read "Choose a resident from
  // the list", an affordance that does not exist on this path: the suggestion
  // buttons render only on a substring match, so after the 403 she can be
  // looking at no list at all (RequestForm's suggestion block).
  //
  // ⭐⭐ T1717 M3 (P1-1) REWROTE BOTH HALVES, and the byte pin moved with it.
  // The first said "turned off in this environment", which names a deployment
  // the nurse cannot see and reads as "you are in the wrong place"; it is the
  // acknowledgement that is unset, not the environment that is wrong. The
  // second sent her to the phone for something no phone call can do - see
  // `newResidentHintReal` above for the full reasoning, which applies to every
  // resident refusal in this file.
  addResidentReadOnly:
    "Adding residents is not switched on here yet. Type a name that is on your home's list.",
  addResidentRoomRequired: 'Enter their room.',
  roomLabel: 'Room',
  equipmentLegend: 'Accessible travel',
  // ⭐⭐ T2174 (Alex + Vicky, 2026-09-17) — the nurse is telling ECMB how to PREP
  // THE BUS, not listing medical kit, so the legend and the three labels move.
  // ⛔ THE STORED KEYS DO NOT: `wheelchair`, `walker` and `escort seat` are what
  // `EQUIPMENT_OPTIONS` validates and what every existing row holds. Relabelling
  // here is a display change and nothing else; there is no migration.
  // ⛔ `oxygen` HAS NO ENTRY HERE, DELIBERATELY. It left the FORM at T2174
  // (`EQUIPMENT_FORM_OPTIONS`), and this map has exactly ONE reader — the map
  // over that list in `screens/RequestForm.tsx` — so an `oxygen` entry could
  // never be reached, and this file refuses an exported shape with no reader.
  // The `?? opt` beside that read is the backstop if the form list ever widens.
  equipmentLabels: {
    wheelchair: 'Wheelchair access',
    'escort seat': 'Carer seat',
    walker: 'Mobility aids',
  } as Record<string, string>,
  // ⭐ T2174 — Vicky's wording, rendered as one `.field-hint` line under each
  // option. Keyed by the STORED key, like the labels above. ⛔ No dash of any
  // kind and no spaced hyphen: `check:copy`'s clause lexer reds on either.
  equipmentDescriptions: {
    wheelchair: 'Ramps, lifts, and designated secure floor spaces.',
    'escort seat': 'Companion or personal assistant seating.',
    walker: 'These are not provided, this is to ensure the bus or vehicle is suitable.',
    // ⛔ `satisfies`, NEVER `as`. `as Record<EquipmentFormOption, string>` is an
    // ASSERTION and silently accepts a SHORT object — measured: deleting the
    // `walker` line typechecked clean, which is the exact hole this annotation
    // was added to close (a missing key rendering as an empty hint). `satisfies`
    // CHECKS, so a missing option is a compile error and the literal type is kept.
  } satisfies Record<EquipmentFormOption, string>,
  destinationLabel: 'Where to',
  destinationPlaceholder: 'Choose a destination',
  // ⛔ EIRCODE, NOT AN ADDRESS, SINCE BOARD 2190 (Alex, 2026-09-18). The box off
  // this option takes a REQUIRED Eircode so the driver has an exact location,
  // with an OPTIONAL place after it for somewhere an Eircode cannot describe.
  // ⚠️ These three strings and `destinationEmptyHint` below were FENCED in
  // `quote-helper/scripts/check-copy.sh`'s CLAUSE_ALLOW while they held a
  // spaced hyphen. The reword IS this board's copy decision, so the fence was
  // REMOVED rather than re-keyed; keep every string here free of ` - `.
  destinationOtherOption: 'Somewhere else',
  destinationTextLabel: 'Destination Eircode',
  destinationTextHint: 'You can add a place after the Eircode, like A98 X123, Bray seafront by the bandstand.',
  // ⭐ A REAL LINK, BY ALEX'S RULING (2026-09-18, board 2190). It shipped as
  // plain words first because the portal had no rendered anchor and the
  // treatment was board 1749's to pick; Alex is the design authority and ruled
  // it a hyperlink, which is what resolves that reservation. A nurse on a ward
  // tablet taps this rather than retyping a URL.
  destinationFinderLabel: 'Find an Eircode at finder.eircode.ie',
  destinationFinderUrl: 'https://finder.eircode.ie/#/',
  // ⛔ THE NEW TAB IS ANNOUNCED, never left to sight alone. Rendered
  // `.visually-hidden` inside the link, so a screen-reader user hears where the
  // tap goes and a sighted user is not told twice.
  destinationFinderNewTab: '(opens in a new tab)',
  destinationHint: 'Not on the list? Choose "Somewhere else" and enter the destination Eircode.',
  // ⛔ THE EMPTY-LIST EXPLANATION (T1683 M4). A home whose account has not been
  // linked and projected yet has NO curated destinations, and a bare dropdown
  // holding nothing but "Choose a destination" reads as a broken screen. It is
  // not broken: the free-text path is the whole journey and always was. So the
  // screen SAYS the list is not set up yet, and points at the option that works.
  // ⚠️ It must not name a fault, a delay or a person - a nurse booking an
  // ambulance run needs the way forward, not an apology.
  destinationEmptyHint:
    'The destination list for your home is not set up yet. Choose "Somewhere else" and enter the destination Eircode. The request reaches Helen exactly the same way.',
  /**
   * ⭐⭐ THE RULING THAT NO WORD HERE MAY ASK FOR A RETURN **TIME** IS REVERSED,
   * AND T1717 M5 IS THE CHANGE THAT SPENDS THE REVERSAL. Alex, 2026-09-02, via
   * `/scope` of the QA report (question 708 in the lane store), reversed board
   * #1681's deferral; boards #1681 and #1683 M7 record it. The pair below asks
   * WHEN the bus brings them back, `portal.requests.return_at` (schema v11)
   * stores it as one instant, and M6 carries that instant to Helen, the booking
   * and the driver. The copy no longer promises a field that does not exist.
   *
   * ⭐⭐ THE PLACEMENT RULING IS REVERSED WITH IT, AND THE NEW PLACEMENT IS PART
   * OF THE COPY. This paragraph used to read: "THE CONTROL SITS WITH 'WHERE TO',
   * NOT WITH THE DATE AND TIME … beside the time controls it reads as the
   * opening of a '…and back at what time?' pair — the exact question the ruling
   * forbids." That reasoning was sound only while the answer did not exist.
   * ⛔ THE WHOLE BLOCK NOW SITS DIRECTLY AFTER THE OUTBOUND DATE AND TIME (DD4),
   * because the question it opens is one this form can now answer: out at this
   * time, back at that one, read as one schedule in one place. Splitting them —
   * the shape of the journey beside the destination, its return time three
   * fieldsets below — would make the nurse hold the outbound in her head while
   * she states the return, on the screen this client bounced off a competitor
   * for having too much on it.
   * ⚠️ ON A RECURRING REQUEST THE TICK KEEPS ITS PLACE AND GAINS NOTHING: it
   * follows the pattern block and no return instant is captured (a return time
   * for a weekly run is a named residual, DD4). The two inputs render only when
   * the tick is on AND the request is one-off.
   *
   * ⛔ REVERSED IN PART, 2026-09-13 (Alex, board 2073, slice 1a): THE RETURN IS
   * SAME-DAY ONLY. `Coming back on` (the return date) left with its control;
   * `Coming back at` is the one return control, composed on the appointment's
   * date. A multi-day return is by phone.
   *
   * ⛔ AND NOT ONE WORD MAY CONTAIN "time" OR "date" — INCLUDING THE RETURN
   * LABEL, which is why it is "Coming back at" rather than the obvious "Return
   * time". The reason the T632 block below carries is unchanged and binds
   * every label and legend on this form:
   * `e2e/tests/portal/portal-smoke.spec.ts` drives this form with
   * `getByLabel('Date')` / `getByLabel('Time')`, a case-insensitive SUBSTRING
   * match, and a second match reds a whole `test.describe.serial` lane. ⚠️ M5 ALSO MADE THOSE FOUR e2e SITES EXACT (DD11) — but exactness on
   * the far side is not a licence here: the two lanes' matchers cannot see each
   * other, so `date`/`time` stay banned in these strings as a floor whatever the
   * e2e directory says. The wording is the SITE's own proven pair
   * (`site/src/components/InquiryForm.astro`), not an invention.
   * ⛔ THE FULL BAN IS DERIVED, NOT LISTED HERE. `test/client/request-form.spec.tsx`
   * reads every non-exact `getByLabel` needle out of `e2e/tests/portal/*.spec.ts`
   * at test time and reds on containment in either direction — the hard-coded
   * list this comment used to point at is exactly what missed a live collision
   * in M4.
   *
   * ⚠️ NO PRICE WORD EITHER. The doubling is the kiosk's, and a nurse never sees
   * a figure (Rule 1) — saying "it costs twice as much" here would be the first
   * customer-facing price in the product.
   */
  // ⛔ DELETED AT T1868 M2 (review): `requestFormCopy.returnTripLabel` was the
  // checkbox's own phrasing and the checkbox is gone, so it had no renderer and
  // left the floor `subjects` array too. ⚠️ NOT to be confused with
  // `myRequestsCopy.returnTripLabel` ('and back'), which is LIVE — MyRequests
  // renders it beside the destination. Two keys, one name, one deletion.
  // ⚠️ UNCHANGED, THOUGH THE RULING ABOVE NOW PERMITS MORE. DD4 allows this hint
  // to name the collection now that the form asks when; it says nothing the two
  // controls below do not, so rewording it would add a sentence to the screen
  // this client bounced off a competitor for having too much on it. Recorded as
  // a decision rather than an oversight.
  // ⛔ REWORDED FOR THE RADIO PAIR (T1868 M2). It said "Tick this…", which named
  // a checkbox this milestone deleted — a hint instructing a nurse to tick
  // something absent from her screen is worse than no hint at all. It now sits
  // inside the trip-type fieldset and explains the choice rather than a control.
  returnTripHint: 'Return means the taxi waits or comes back for them afterwards.',
  // ⛔ THE SITE'S OWN PROVEN PAIR, and the wording is load-bearing twice over:
  // neither string contains `date` or `time` (the e2e substring hazard the
  // docblock above states), and neither invents a phrasing this business has
  // not already put in front of a customer (`site/src/components/InquiryForm.astro`).
  // ⭐⭐ T1868 M2 — THE RETURN PAIR'S OWN GROUP NAME. Removing the checkbox took
  // this fieldset's accessible name with it: its legend used to be the tick's own
  // phrasing, and a group holding two date/time controls with NO name is exactly
  // the "two top-level fields" shape ux-review rejected at T1717 M5. A sighted
  // nurse still sees the border; a screen-reader user got nothing.
  // ⛔ CONTAINS NEITHER `date` NOR `time`, like its label below and for the
  // same measured reason: the e2e lane drives this form with substring-matched
  // `getByLabel`, and the floor `subjects` arm reds on a collision either way.
  // ⛔ `returnDateLabel` ('Coming back on') LEFT 2026-09-13 with the return date
  // control (Alex, board 2073): the return is same-day only.
  returnWhenLegend: 'When they come back',
  returnTimeLabel: 'Coming back at',
  payerLegend: 'Who is paying',
  // `self` reads "Cash / card" (board 2186, Alex 2026-09-18): staff say cash, the passenger pays
  // by card. ⛔ THE STORED TOKEN IS STILL `self` — this is a relabel, never a new payer kind, and
  // the kiosk's PAYER_WORDS must move with it (payer-vocabulary-parity.spec.ts pins them equal).
  payerLabels: { hse: 'HSE / Rehab', house: 'House account', self: 'Cash / card' } as Record<string, string>,
  // The double-check on the payment method, shown only when Cash / card is chosen (boards 2186, 2706).
  // ⛔ NO SPACED HYPHEN (check-copy's CLAUSE) and NO "date"/"time" substring (the subjects FLOOR
  // at request-form.spec.tsx:2194-2202) — both bans apply to these two strings.
  payerConfirmLabel: 'Yes, the passenger is paying by cash or card',
  payerConfirmError: 'Tick the box to confirm how the passenger is paying.',
  /**
   * ⭐⭐ ux-review (High) — PREFILL WAS SILENT, AND SILENCE IS THE DEFECT.
   *
   * ⛔ Picking a resident filled three controls with no announcement and no
   * visible change. A sighted nurse can miss it; a screen-reader user gets
   * nothing at all, and the form she is about to send now carries a third
   * party's phone number she never saw arrive. This is a `role="status"` line,
   * so it is announced politely rather than interrupting her typing.
   *
   * ⛔ IT NAMES WHAT WAS ACTUALLY FILLED, never a fixed sentence — and since
   * T1868 M6 there is exactly ONE prefilled field, so it names the room.
   * ⚠️ THE PARAMETER IS GONE RATHER THAN DEFAULTED. It took
   * `'room' | 'kin' | 'both'` because the next-of-kin pair could fill without the
   * room or with it; keeping a one-valued parameter would leave a caller free to
   * pass a case this sentence can no longer describe.
   * ⛔ THE RULE IT ENCODES SURVIVES THE REDUCTION and must be restored with any
   * second prefilled field: claiming a field was filled when it was not sends a
   * nurse past a required control that is still empty, which is the exact
   * failure the announcement exists to prevent.
   * ⚠️ AND IT IS A SENTENCE, NOT A LIST WITH ONE ITEM (ux round, T1868 M6). The
   * three-way template rendered as "Filled in from your list: room." — a colon
   * and a bare noun, which a screen reader utters as a data dump rather than as
   * prose. A one-item list is not a list; write the sentence.
   * ⛔ AND IT ENDS BY ASKING HER TO CHECK. These are values copied from a list
   * that may be out of date.
   * ⚠️ NO NEEDLE: it contains none of `date`, `time`, `resident`, `where to`,
   * `pin`, `hse` — checked against the derived e2e vocabulary, same rule as the
   * labels (DD11). "list" is not a needle.
   */
  prefilledNotice: (): string => "Filled in their room from your list. Check it is still right before you send.",
  // ⛔⛔ SEVEN NEXT-OF-KIN STRINGS STOOD HERE AND ARE DELETED (T1868 M6):
  // `nextOfKinNameLabel`, `nextOfKinNameHint`, `phoneLabel`, `phonePlaceholder`,
  // `phoneHint`, `phoneUseExample` and `phoneHintReal`. The controls they named
  // are gone from this form and from the roster tab, so every one of them was a
  // string with no render site.
  // ⚠️ ONE LESSON IS WORTH MORE THAN THE STRINGS AND IS KEPT HERE: `phoneHintReal`
  // was deliberately COUNTRY-AGNOSTIC because the obvious wording is dangerous.
  // "Swap the leading 0 for +353" is the natural draft, and a nurse whose contact
  // is in London types 07700 900123, follows it literally, and produces
  // +3537700900123 — which PASSES a shape check and is a WRONG NUMBER. Any future
  // phone-entry copy on this service inherits that: name TWO country codes, so the
  // example reads as a variable rather than as the answer, and say to drop the
  // trunk 0, because +3530871234567 also passes and is silently wrong.
  // ⭐⭐ T1868 M2 — THE TRIP TYPE, AND THE LEGEND CHANGED WITH IT. The pair used
  // to answer "when" (one-off vs regular) under the legend `When`; it now answers
  // "does the bus bring them back", so a legend reading `When` above One way /
  // Return would be asking a different question from the one the options answer.
  // ⛔ THE KEY NAMES ARE DELIBERATELY UNCHANGED (`oneOffOption` / `weeklyOption`).
  // Renaming them is a wider edit than this milestone's fence, and every one of
  // the three e2e specs plus the floor `subjects` array keys off these names;
  // the VALUES are what LCC asked to change. Recorded so the mismatch between
  // `weeklyOption` and the word `Return` reads as a decision, not a leftover.
  // ⭐ T1868 M2 (ux review) — 'Does the bus bring them back' was a YES/NO question
  // whose options answer 'One way' / 'Return', so a nurse had to translate before
  // answering. The cost of a wrong tap here is asymmetric — a mis-tapped trip type
  // is not correctable in the portal (Q112) — so the legend now matches the SHAPE of
  // the answer, in the client's own vocabulary (this milestone is titled 'Trip type').
  scheduleLegend: 'Trip type',
  oneOffOption: 'One way',
  weeklyOption: 'Return',
  // ⛔ THE LEGEND NAMES THE PAIR THE RADIO USED TO NAME (2026-09-13, Alex, board
  // 2073). It carries neither "date" nor "time" because the label floor
  // (`test/client/request-form.spec.tsx`, the `subjects` arm walks legends too)
  // and the e2e needle floor forbid them near the Date / Time inputs.
  appointmentLegend: 'When is the appointment?',
  dateLabel: 'Date',
  dateHorizonHint: 'Within the next 4 weeks. For anything further out, ring East Coast Minibus.',
  timeLabel: 'Time',
  // T632. ⛔ NOT ONE WORD NEAR THE TIME INPUT MAY CONTAIN "time", in any case.
  // e2e/tests/portal/portal-smoke.spec.ts drives this form with
  // getByLabel('Time'), which is a case-insensitive SUBSTRING match: a label or
  // legend reading "the appointment time" would match both it and the time
  // input, and the resulting strict-mode violation sits inside a
  // test.describe.serial, so it would blank every downstream test in the
  // lane. The disambiguation therefore never lives in the time input's own
  // label -- which also means that label stays neutral and no cross-project
  // label collision is created.
  //
  // ⛔ THE "arrive or collect" RADIO THAT USED TO CARRY THE DISAMBIGUATION IS
  // GONE (2026-09-13, Alex, board 2073, slice 1a). It asked the nurse what the
  // time meant; the form now asks the appointment time only, the meaning is
  // sent as the constant `appointment`, and the collection is derived from the
  // home's own offset and reaches Helen without the nurse ever seeing it. The
  // legend above is what names the pair now. (`timeMeaningRowCopy` further
  // down is the stored meaning's READ-side row and is unrelated to this form.)
  // ⛔ THE PICKUP PAIR AND ITS COPY LEFT 2026-09-13 (Alex, board 2073, slice
  // 1a): `pickupWhenLegend` / `pickupDateLabel` ('Picked up on') /
  // `pickupTimeLabel` ('Picked up at') / `pickupDerivedNotice` /
  // `pickupStaleWarning`. The pickup is derived from the home's offset in
  // `request-form-logic.ts` and never rendered; T1868 M5's "named plainly"
  // policy for a second date/time pair is in git, not rotting here.
  //
  // ⭐⭐ THE COLLECTION IS RENDERED AGAIN (board 2188, Alex 2026-09-18), which
  // REVERSES the "without the nurse ever seeing it" clause two notes above. It
  // is READ-ONLY: a derived line, never a control, so nothing here revives the
  // retired pickup pair or its labels.
  // ⛔⛔ NEITHER KEY BELOW MAY CONTAIN "time", IN ANY CASE, and that is the T632
  // rule at the top of this block, not a fresh preference: these two strings
  // render INSIDE the appointment fieldset, beside the very input that
  // `getByLabel('Time')` matches by case-insensitive SUBSTRING. "Collection
  // time" here would match both and blank the downstream lane.
  // ⇒ `Collection` alone is the word here, which is also the register's own
  // (`packages/ui/src/format-when.ts` `MEANING_WORDS.collection`).
  // ⛔⛔ BUT THE FOUR SURFACES DO **NOT** ALL READ ONE WORD, AND THIS COMMENT
  // CLAIMED THEY DID (plan-alignment, board 2188 rework 2). What actually ships:
  // this form and Helen's Ops table say **Collection**; the nurse's card and
  // reception's card say **Pickup**, because `CardRequest` renders a FIXED `<dt>`
  // (`packages/ui/src/Card.tsx:472`) that no caller supplies. The two words are
  // already one meaning in two registers in this repo — the inline line says
  // `Collection` inside a sentence, the card's `<dt>` says `Pickup` beside a big
  // clock — which is a convention `my-requests-when.spec.ts` pins deliberately,
  // not an accident of this row. Unifying them is a copy decision for a sitting
  // and is recorded on the R-2073-c row, NOT taken here.
  collectionDerivedLabel: 'Collection',
  // ⚠️ No apostrophe, straight or curly: this file carries none, and a curly one
  // would have been the only one in it. Reworded rather than escaped.
  collectionDerivedHint: 'Worked out from how long before an appointment your home asks the bus to call.',
  frequencyLabels: {
    weekly: 'Every week',
    fortnightly: 'Every two weeks',
    every_n_weeks: 'Every few weeks',
    monthly_date: 'Monthly, on a date',
  } as Record<string, string>,
  weekdayLabels: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  notesLabel: 'Notes for the driver (optional)',
  submitButton: 'Send request',
  submittingButton: 'Sending',
  successTitle: 'Request sent',
  successBody: (residentName: string, destinationName: string): string =>
    `This request for ${residentName} to ${destinationName} is on its way to Helen at East Coast Minibus.`,
  /**
   * ⛔ T1101 — THE FAILURE VARIANT REPLACES THE HEADING AND THE BODY ABOVE, it
   * does not sit beneath them. `successBody` asserts the request "is on its way
   * to Helen"; when the office alert reached NOBODY that sentence is false, and
   * a notice contradicting the paragraph above it is worse than no notice.
   *
   * ⛔ IT LEADS WITH "SAVED", NOT WITH THE FAILURE. A bare "we could not reach
   * the office" invites the one action that makes things worse: sending the
   * same trip again, so Helen gets two requests for one journey and cannot tell
   * which is real. The order is saved, then do not resend, then ring.
   *
   * ⚠️ Shown ONLY on total failure. Partial delivery shows nothing new: somebody
   * was told, the nurse cannot act on "one of two alerts failed", and training
   * her to see a notice she must ignore is how the notice that matters gets
   * ignored too.
   */
  alertFailedTitle: 'Request saved, office not reached',
  alertFailedBody: (residentName: string, destinationName: string): string =>
    `This request for ${residentName} to ${destinationName} is saved here, but we could not get word to the office.`,
  /**
   * ⭐ T1683 M3 (A1) - THE JOB REFERENCE, NOT `#<requestId>`. This sentence is
   * read out loud by a ward nurse on a phone call, which is the whole reason A1
   * exists, and a raw row id is the wrong string for it twice over: it is not
   * what Helen's screen or the office alert now show, and one job with two
   * spellings is exactly the failure this milestone removes.
   *
   * ⛔ AND THE `null` ARM IS NOT DEAD CODE. Every request written before M3
   * carries `job_ref IS NULL` for ever - there is no backfill - so this copy
   * must be able to say the true thing without a reference rather than print
   * "The reference is ." at a nurse who has just been told to ring.
   */
  /**
   * ⚠️⚠️ THE P1-1 CARVE-OUT, STATED SO THE SWEEP STOPS RE-RAISING IT — T1717 M7,
   * Captain deltas #28, which read this sentence as P1-1 residue.
   *
   * P1-1's rule is "no sentence in this file may offer Helen or the phone as the
   * remedy FOR A RESIDENT", and its reason is that the call CANNOT WORK:
   * `POST /api/residents` is the only writer of a roster row and no operator
   * screen calls it, so a nurse sent to the phone about a resident gets nowhere.
   * ⇒ NEITHER HALF APPLIES HERE. This is the REQUEST path, the failure is that
   * the office was not told about a journey, and ringing the office is the one
   * thing that DOES fix that — it is the remedy, not a deflection. Rewriting it
   * to name a capability would leave a saved request nobody knows about, which
   * is the same false-promise defect P1-1 removed, pointing the other way.
   * ⇒ CHECKED, NOT CHANGED. If the rule is ever widened past residents, widen it
   * with a reason that survives this paragraph.
   */
  alertFailedNotice: (jobRef: string | null): string =>
    jobRef === null
      ? 'Your request IS saved. Please do not send it again. Ring East Coast Minibus and tell them ' +
        'the resident and the date.'
      : `Your request IS saved. The reference is ${jobRef}. Please do not send it again. ` +
        'Ring East Coast Minibus and give them that reference.',
  /** ⭐ T1683 M3 (A1). The reference on the ORDINARY success path. Spelled out
   *  as a sentence rather than a bare code so a nurse reading it aloud knows
   *  what she is reading; the same string appears on Helen's screen, on the
   *  office alert and on the home's invoice. */
  jobRefLabel: (jobRef: string): string => `Job reference: ${jobRef}. Quote this if you ring us.`,
  /**
   * ⭐⭐ P1-2 (T1717 M4, DD3) — THE ONLY WAY A REQUEST EVER CHANGES THE ROSTER.
   *
   * ⛔ IT IS SHOWN ONLY WHEN SOMETHING ACTUALLY DIFFERS, and it starts UNTICKED.
   * Prefill copies the roster's room and next-of-kin pair into the form; a nurse
   * correcting one of them for THIS journey (a temporary room, a sister covering
   * for the usual contact) must not silently rewrite the standing record. So the
   * control appears only once a prefilled value has been changed, and the change
   * reaches `portal.residents` only on an explicit tap.
   *
   * ⚠️ THE LABEL SAYS "ALSO", which is the whole meaning: the request carries
   * what she typed either way. Ticking adds a second act, it does not redirect
   * the first.
   *
   * ⛔⛔ IT SAYS NEITHER "update" NOR "resident", AND THAT IS DD11, NOT STYLE.
   * The e2e portal lane matches labels by case-insensitive SUBSTRING, and the
   * first draft of this string — "Also update the resident list" — collided with
   * the live vocabulary TWICE: "up**date**" contains the `Date` needle and
   * "resident" is the `Resident` needle outright. MEASURED, not reasoned about:
   * `test:portal` died with `strict mode violation: getByLabel('Date') resolved
   * to 2 elements` — this checkbox and `rf-date` — inside a
   * `test.describe.serial`, which stranded every downstream test in that file.
   * ⛔ THE FIX WAS THE LABEL, NEVER THE LOCATOR. Adding `{ exact: true }` to the
   * lane's `getByLabel('Date')` would have been editing the check to fit the
   * code; this string is this milestone's to choose, and the vocabulary is not.
   * (DD11 does schedule that `{ exact: true }` change — for M5, on its own
   * merits, and it is behaviour-preserving there because the live label is
   * literally `Date`.)
   * ⚠️ CHECKED AGAINST THE DERIVED NEEDLE SET, not against memory:
   * `test/client/request-form-prefill.spec.tsx` reads every `getByLabel(...)` in
   * `e2e/tests/portal/*.spec.ts` at test time and reds on containment in EITHER
   * direction, for EVERY label this milestone adds. Reverting this string to the
   * draft reds it with both collisions named.
   */
  updateRosterLabel: "Also save these details to your home's list",
  updateRosterHint: 'Only tick this if the new details are their usual ones from now on.',
  /* ⛔ NO REFUSAL STRING OF ITS OWN, DELIBERATELY. A 403 on the write-back means
     the SAME capability is shut as on the roster tab, so the form renders
     `myResidentsCopy.writesClosed` — the one string — rather than a second
     sentence saying the same thing in different words. Same reason the 409 arms
     below reuse `alreadyOnList`, `listFull` and `removedOffer`: a nurse who
     meets a refusal twice must read one fact, not two. ⚠️ The request itself
     still goes; the write-back is the optional half. */
  doneLockButton: 'Done, lock',
  anotherRequestButton: 'Another request',
  /**
   * ⭐⭐ T2075 M3 (plan D12, R-3) — WHO IS REQUESTING, THE FIRST CONTROL ON THE
   * FORM. A `group` account picks a name from its own group's active staff
   * (recently used first); reception and admin pick a group, then a name.
   * ⛔ APPENDED HERE, AT THE END OF `requestFormCopy`, so the one line-keyed
   * entry below (`accessReviewAccessNo`, keyed by line number in
   * `quote-helper/scripts/check-copy.sh`) moves by exactly this block: re-key
   * it there, never reword it.
   * ⛔ NO LITERAL HERE MAY CONTAIN `date`, `time`, `Resident`, `Return`, `PIN`,
   * `Where to`, `One way` or `HSE` as a substring (the label floor in
   * `test/client/request-form.spec.tsx` and the e2e needle floor), no spaced
   * hyphen, no em or en dash, and the word is "repeating", never the three the
   * copy census bans.
   * ⚠️ `requesterRequired` IS THE VALIDATOR'S OWN WORDING, pinned equal to
   * `shared/request-schema.ts`'s literal by `request-form.spec.tsx`: the form
   * renders whatever `validateRequestInput` said (server or browser, the same
   * function), so the sentence lives once on the wire and this key is the copy
   * file's record of it, the way every other refusal string here is graded.
   */
  requesterLabel: 'Who is requesting?',
  // ⭐⭐ T2174 (Alex, 2026-09-17) — THIS QUESTION IS ONLY EVER ASKED OF RECEPTION
  // AND ADMIN, who book on behalf, so the label now says so. A group sign-in
  // never sees it: `groupPicksFirst` is `role !== 'group'` and the field renders
  // only when that is true, its group coming from the session's account.
  // ⛔ The rename is load-bearing for the label floor in
  // `test/client/request-form.spec.tsx`: the OLD two-word label is a live e2e
  // `getByLabel` needle and the new string CONTAINS it, so both e2e sites must
  // move to this exact literal in the same change or the floor arm reds.
  // (Playwright's `getByLabel` is a case-insensitive SUBSTRING match, so the e2e
  // keeps passing either way — the red lands in vitest, not in the lane you
  // would think to check.)
  requesterGroupLabel: 'On behalf of which group?',
  requesterPlaceholder: 'Type a name',
  requesterRequired: 'Choose who is requesting this trip.',
  requesterEmpty: 'No names in your group yet. Type your name and tap Add.',
  requesterRecentHint: 'Names used recently come first.',
  // ⭐ T2075 M3 (ux-review round 1, WARN): the group select's OWN empty option.
  // It reused `requesterPlaceholder`, so "Which group?" sat one line above a
  // dropdown reading "Choose a name" — a label contradicted by the control
  // under it. Appended here so only the line key below moves, once more.
  requesterGroupPlaceholder: 'Choose a group',
  // ⭐⭐ T2088 M4 (plan D3, R-1) — THE RETURN TIME MAY BE LEFT BLANK, and the
  // hint is the whole of the control: no checkbox, no second question. Blank
  // means "to be confirmed", the form sends the `to-confirm` marker (ADR-0006),
  // and Helen rings. ⛔ CONTAINS NEITHER `date` NOR `time`, like every string
  // the return fieldset renders, because the label floor in
  // `test/client/request-form.spec.tsx` lists hints beside labels and the e2e
  // `getByLabel` needles are substring matches. ⛔ No dash of any kind and no
  // spaced hyphen: `check:copy`'s clause lexer reds on either.
  // ⭐⭐ T2174 (Alex, 2026-09-17) — THE RING-BACK PROMISE IS GONE, THE AFFORDANCE
  // STAYS. Only the second sentence was removed — it promised a ring-back, and
  // the form must not promise a call. ⛔ The FIRST sentence is the D3 affordance
  // this docblock describes — it carries the meaning of a blank, which is why D3
  // rejected a second checkbox and why the `aria-describedby` wiring references
  // it in BOTH states (WCAG 3.3.2 / 1.3.1, Level A). Deleting it would have
  // retired an accessibility control the ruling never asked to retire.
  // Helen still rings on a `to-confirm` marker; the form simply no longer says so.
  returnTimeOptionalHint: 'Leave this blank if you do not know yet.',
  // ⛔⛔ THE VALIDATOR'S MARKER-WITH-A-TIME REFUSAL, WORD FOR WORD, AND THAT
  // IS THE WHOLE POINT (T2088 M4 security review, Low). The plan's Step 1 value
  // was a SECOND phrasing of a refusal the shipped validator already words
  // (`shared/request-schema.ts`, the `toConfirm` arm), and the two differed; the
  // one that reaches a nurse today is the validator's, so this constant is now
  // it. ⛔ THE TWO CANNOT DRIFT: `request-form.spec.tsx` derives the validator's
  // message by RUNNING `validateRequestInput` on a marker-beside-a-time payload
  // and asserts equality with this string, so a reword of either side reds. The
  // literal is not imported because the validator holds it inline at its arm,
  // and moving it to an export is that file's milestone, not this one.
  // ⚠️ UNREAD ON ANY SURFACE TODAY, by construction rather than oversight:
  // `assembleRequestPayload` sends the marker ONLY when the time is blank, so no
  // browser path can produce marker-and-time, and the message a hand-built POST
  // earns is the validator's own, as for every other key. Its reader is the
  // first surface that states the conflict CLIENT-SIDE, before the POST.
  // ⚠️ RE-DERIVE BY FILE, NOT BY LINE, and the shape of the command is the
  // lesson (M4 code-review): a line count over `portal/src` alone reported 5
  // — the gitignored built bundle `src/server/public/client.js` and its `.map`
  // are under `src/`, and a comment that names the key self-matches. Ran
  // 2026-09-16 → exactly 2 files, this one and the spec that pins it:
  // `/usr/bin/grep -rl returnAtStateConflict portal/src portal/test --include='*.ts' --include='*.tsx'`
  returnAtStateConflict:
    'You gave a return time and also said it is to be confirmed. We have not dropped the time. Send the time, or clear it and say it is to be confirmed.',
};

/** Row-level rendering of what a request's time refers to (T632).
 *
 *  Rendered PER ROW at both display ends, never as a column header: the
 *  marker varies per request, so a static header could not express it -- that
 *  is the single-label solution this design exists to replace.
 *
 *  `notStated` is load-bearing, not a fallback. Every request written before
 *  migration v4, and any one-off request where the nurse skipped the choice,
 *  carries NULL. Rendering that as blank would let Helen read a bare time as
 *  though a meaning had been given; naming it keeps the gap visible. */
export const timeMeaningRowCopy = {
  appointment: 'appointment',
  collection: 'collection',
  notStated: 'not stated',
} as const;

export const myRequestsCopy = {
  // ⛔ Names the OBJECT, not the widget: the nurse and Helen are looking at one
  // request on one journey, which is the whole claim ADR4-D11 makes.
  lifecycleLabel: 'Progress of this request',
  title: 'My requests',
  emptyState: 'Nothing coming up.',
  /** ⭐ T1723 M7 (DESIGN.md §7.2) — an empty state says why AND what to do next,
   *  and points at the tab that does it by its net-locked name. */
  emptyStateNext: 'Send one from New request and it will show here.',
  /**
   * ⭐ T1723 M7 — THE SENTENCE THE STATUS PILL LEADS (Card A, Vicky qa-2026-09-05).
   * Keyed by the WIRE value. ⛔ THE WORD IS NOT HERE: it comes from
   * `labelFor('request', status)` in `@ecmb/ui` (Words 3, sitting 2026-09-10),
   * so `new` and `priced` share a sentence exactly as they share the word
   * `Sent` — the nurse never sees a quote stage on account-priced work. The
   * `declined` line sends her to the phone, which is the standing rule and the
   * only channel that can act (the office declined; a form cannot undo that).
   * ⛔ NO MONEY WORD in any of these: `my-requests-when.spec.tsx` sweeps the
   * rendered nurse screen for one, with a planted positive control.
   */
  waitingOn: {
    new: 'Waiting on East Coast Minibus to confirm it.',
    priced: 'Waiting on East Coast Minibus to confirm it.',
    confirmed: 'The taxi is booked.',
    declined: 'East Coast Minibus cannot take this one. Ring them to talk it through.',
    cancelled: 'You stopped this request.',
    // ⛔ `satisfies`, NOT `as Record<string, string>` (security-review at M7): the
    // compiler is the guard, as it is for `REQUEST_LABELS` and the tone maps. A
    // sixth request status without a sentence fails `tsc` here instead of
    // rendering a pill with nothing beside it.
  } satisfies Readonly<Record<RequestStatus, string>>,
  /** The card's `Time` row when the stated time cannot be read at all. The
   *  formatter's own rule: a missing time is SAID, never left blank. */
  timeNotStated: 'Not stated',
  /**
   * ⭐ T1723 M7 — THE DAY FILTER (plan M7 item; SC-C1..C4). UX, not a control:
   * every row has already crossed the wire (`listRequestsForHome` has no LIMIT),
   * and `All` is the default, so nothing is hidden until the nurse asks.
   * ⚠️ "This week" is the Dublin calendar week, Monday to Sunday (an assumption
   * recorded in DECISIONS.md / OPEN-QUESTIONS.md, not a ruling). ⛔ NO
   * `regular` / `recurring` here: the portal copy census bans both; the settled
   * word for a legacy pattern row is "repeating" (`cancelRecurringButton`).
   */
  filterLegend: 'Show',
  filterToday: 'Today',
  filterWeek: 'This week',
  filterAll: 'All',
  /** ⭐ impeccable W4 at M7: an empty bucket says WHY and WHAT NEXT (§7.2), and
   *  says "dated" so it does not contradict a repeating trip listed beneath it. */
  filterEmptyToday: 'No dated trips today. Tap All to see every request.',
  filterEmptyWeek: 'No dated trips this week. Tap All to see every request.',
  /** ⭐ impeccable W3 at M7: what a filter tap did, for a screen reader (a
   *  `role="status"` line; the pills only announce their own pressed state). */
  filterShown: (n: number): string => (n === 1 ? '1 dated trip shown.' : `${String(n)} dated trips shown.`),
  /** ⭐ impeccable W6 at M7: the dated list's own heading, visually hidden, so the
   *  outline reads h1 My requests > h2 <bucket> > h3 cards > h2 Repeating trips >
   *  h3 cards, instead of skipping from h1 to h3. Keyed by the filter. */
  bucketHeading: { today: 'Today', week: 'This week', all: 'All requests' } as const,
  /** The group a legacy pattern row is listed under, in EVERY bucket: a standing
   *  run has no single day, so it never vanishes and never claims one. */
  repeatingHeading: 'Repeating trips',
  /** ⭐ T1723 M7 (DESIGN.md §7.3) — the impact clause beside a load failure. True
   *  by construction: this screen only reads. */
  loadErrorImpact: 'Your requests are safe. Nothing has been lost.',
  /** ⭐ T1683 M3 (A1). ⛔ The row omits this line entirely when the request has
   *  no reference (every request written before M3), rather than rendering the
   *  label over an empty value. */
  jobRefLabel: (jobRef: string): string => `Ref ${jobRef}`,
  /** ⭐ T1683 M7 (A4) — READ BACK TO THE NURSE WHAT SHE ASKED FOR. The row omits
   *  it entirely on a one-way job rather than printing "One way": absence is the
   *  common case and a line on every row would be noise on the one screen this
   *  client bounced off a competitor for having too much of.
   *  ⭐ T1717 M5 GAVE IT A TIME - board #1681's deferral was reversed by Alex
   *  on 2026-09-02 via `/scope` of the QA report (question 708 in the lane
   *  store). `MyRequests.tsx` now renders `returnAt` beside this phrase inside
   *  the destination cell; a return job with no instant (a RECURRING one, or any
   *  request written before schema v11) reads this phrase alone, exactly as it
   *  did before M5. Every return render stays gated on `returnTrip`, so a flip to
   *  one-way hides the phrase and the instant together. */
  returnTripLabel: 'and back',
  /** ⭐⭐ T2088 M4 (plan D3, R-1) — WHAT STANDS WHERE THE INSTANT WOULD, on a
   *  row whose `returnAtState` is `'to-confirm'`: "Beacon Clinic and back time
   *  to confirm", one space, no comma, no dash (DESIGN.md §7.6's card grammar
   *  runs the phrase and its time together, and this is the time's slot).
   *  ⛔ RENDERED ONLY FOR `'to-confirm'`. A timeless return whose state is
   *  `'unset'` (a pre-v14 row, or a standing run whose pattern captures no
   *  instant) is NOT to be confirmed; it is a row that never recorded one, and
   *  it keeps reading the bare phrase. `HomeRequests.tsx` reads this same key,
   *  so the two nurse-side lists cannot phrase the one state two ways. */
  returnToConfirm: 'time to confirm',
  // Cancel / stop (P5), New or Priced only since board 2788 R-4; a Confirmed card
  // asks instead (`requestChangeCopy`). A repeating trip has no date to change.
  //
  // ⛔ "REPEATING", NOT "REGULAR" — T1868 M7's copy audit. The word is not a
  // preference: M2 already shipped this concept's refusal as *"Repeating trips
  // are not booked through this form. Ask for One way or Return, one journey at
  // a time."* (`shared/request-schema.ts`), so a nurse who met that sentence and
  // then met a button called "Stop this regular trip" would be asked to
  // translate between two words for one thing — which is the LCC complaint this
  // vocabulary exists to remove.
  // ⚠️ THE BUTTON IS STILL REACHABLE, so this is a rename and not a deletion.
  // `MyRequests.tsx:224` renders it whenever `item.schedulePattern` is set, and
  // while M2 stopped any NEW request carrying a pattern, every row written
  // BEFORE M2 still has one and still appears on her list.
  cancelButton: 'Cancel this request',
  cancelRecurringButton: 'Stop this repeating trip',
  /**
   * ⚠️ T1011 M6 CHANGED THIS SENTENCE, and the change is the point rather than a
   * tidy-up. It used to read "This tells East Coast Minibus not to run it",
   * which is the exact claim the code cannot keep: cancelling writes
   * `portal.requests.status` in THIS service's database, and whether a bus is
   * driven (and billed) is decided by `bookings.status` in the kiosk's. Two
   * databases, no join key, and this service makes no outbound calls at all. It
   * now says what it does do, and `cancelScopeNote` says what it does not.
   */
  cancelConfirmPrompt: 'Sure? This tells East Coast Minibus you no longer want it.',
  /**
   * ⭐⭐ T1011 M6 — THE SHARPEST INSTANCE OF THE LIE, BECAUSE THIS ONE IS
   * CUSTOMER-FACING. A home cancels, is shown a success, believes the trip is
   * off, and the run is still driven and still billed.
   *
   * ⛔ IT SENDS HER TO THE PHONE, and that is the correct remedy here rather than
   * a fallback: this service cannot reach the kiosk, so a person is the only
   * channel that exists. It is also the project's standing rule (the phone never
   * closes) and the audience is nurses on a ward with a trip that may be today.
   *
   * ⛔ IT DOES NOT SAY "you will still be billed" FLATLY, because for a request
   * that never became a booking that would be false and frightening. "May still
   * be" is the honest shape: we cannot tell from this side which it is, and
   * saying so is what makes the ring-us instruction reasonable rather than
   * bureaucratic.
   *
   * ⭐⭐ T2088 M6 (R-3) — AND THE OFFICE IS NOW TOLD, which it was not when this
   * sentence was written: a portal cancellation reached the home's own reception
   * and stopped there. Saying so is the difference between "shout down a well"
   * and "we have your message"; Alex ruled the channel on 2026-09-16 because the
   * homes cancel a lot and no bus may leave for nothing.
   * ⛔ THE TWO "MAY STILL" CLAUSES STAY, WORD FOR WORD, AND THE NEW CLAUSE DOES
   * NOT SOFTEN THEM. Being told is not being stood down: an email reaches an
   * inbox, and only Helen's click stops a booking in the OTHER database. A nurse
   * who read "the office is told" and stopped there would be back in the exact
   * belief this string exists to correct — which is why the limit follows the
   * reassurance in the same breath, and why the phone instruction is last.
   */
  cancelScopeNote:
    'The office is told straight away, but this does not stop the taxi on its own. If the trip is '
    + 'already booked in it may still run, and may still be billed, so please ring East Coast '
    + 'Minibus as well.',
  /**
   * ⛔⛔ T2088 M6 rework — THE RETRACTION, FOR THE ONE CASE WHERE THE SENTENCE
   * ABOVE IS FALSE. `cancelScopeNote` opens with an UNCONDITIONAL promise ("The
   * office is told straight away") and the screen had no way to take it back:
   * `MyRequests.tsx`'s cancel path never read `alertFailed`, while the create
   * path in `RequestForm.tsx` has read its own since T1101. So on the one run
   * where nobody at East Coast Minibus got the message, the nurse was told they
   * had — which is this string's own history, and the reason its comment calls it
   * the sharpest instance of the lie.
   *
   * ⛔ IT IS `alertFailedNotice`'s REGISTER, DELIBERATELY: what IS true first
   * ("cancelled here"), then the plain failure, then the phone. A nurse who reads
   * only the first clause is not left believing something false.
   * ⛔ THE TWO "MAY STILL" CLAUSES ARE REPEATED HERE RATHER THAN ASSUMED
   * INHERITED. She read them inside the confirm block, before the tap, and this
   * paragraph replaces that block on a card whose row has since gone terminal —
   * so the consequence has to travel with the correction or it does not travel.
   * ⛔ NO REFERENCE IN IT, unlike the create path's: the cancel route answers
   * `{ ok: true }` and carries no job reference, and a sentence naming one this
   * screen does not have would be a blank where a code should be.
   */
  cancelAlertFailedNotice:
    'This request IS cancelled here, but we could not get word to the office. Please ring East '
    + 'Coast Minibus and tell them, because the trip may still run, and may still be billed, '
    + 'until somebody there stands it down.',
  cancelConfirmYes: 'Yes, cancel it',
  cancelConfirmKeep: 'Keep it',
  /**
   * ⭐⭐ T2088 M5 (R-1's last clause, plan Step 4) — THE RETURN TIME, SET AFTER
   * THE FACT. A nurse who ticked Return and said the time was not yet known can
   * add it here, once, while the request is still hers to change.
   *
   * ⛔ THE CONTROL IS THE CANCEL LIMB'S SHAPE, NOT A MODAL: the button arms an
   * in-place prompt with a Save/Not-yet pair, in the slot the button was in.
   * The screen composes `@ecmb/ui` and draws nothing, so this takes no new
   * component and no new variant.
   * ⛔ `setReturnTimePrompt` IS THE TIME CONTROL'S OWN `<label>`, not a
   * paragraph beside it: the question a nurse reads and the name a screen
   * reader announces must be one sentence, and the e2e net queries it by that
   * name (`getByLabel`).
   */
  setReturnTimeButton: 'Add the return time',
  setReturnTimePrompt: 'What time does the bus bring them back?',
  setReturnTimeSave: 'Save the time',
  setReturnTimeKeep: 'Not yet',
  /**
   * ⛔⛔ WHAT STANDS WHERE THE CONTROL WOULD, on a request Helen has already
   * confirmed. She is never offered a control that the route would refuse: a
   * `confirmed` request has crossed to the kiosk, and this service cannot reach
   * the booking that was made there — the same honest posture, and the same
   * remedy, `cancelScopeNote` already takes for cancellation. The phone never
   * closes, and a person is the only channel that exists from this side.
   */
  setReturnTimeCrossedNote: 'East Coast Minibus has this one in hand. Ring them to add the return time.',
  /**
   * ⛔⛔ ONE SENTENCE FOR SEVERAL CAUSES, BECAUSE THE SERVER CANNOT TELL THEM
   * APART AND THIS SCREEN MUST NOT INVENT ONE. A 409 means the row did not move:
   * the time was already set (a second tap), Helen crossed the request between
   * this card rendering and the tap, the status moved on, or the row is gone.
   * "May already have this one in hand" is the honest shape across all of them —
   * it claims nothing about which, and it sends her to the phone.
   */
  setReturnTimeFailed:
    'We could not add the time. East Coast Minibus may already have this one in hand, so please ring them.',
  /**
   * ⛔ THE PILL AND THE LIFECYCLE SPINE MUST AGREE, WORD FOR WORD. Ruled at
   * T1033 M7 by `ux-review` and executed at M8.
   *
   * The pill and the spine sit in the SAME `<li>`, adjacent, describing one
   * fact two ways. `new` and `confirmed` already matched their stage exactly;
   * only `priced` disagreed, rendering "Priced" beside a stage that says
   * "Quoted" (`nurse-request-projection.ts` / `operator-lifecycle.ts`, both
   * `label: 'Quoted'`). Two words for one state reads as a bug on a nurse's
   * screen even though the underlying status is correct — so the label moves
   * to the spine's word rather than the spine moving to the label's.
   *
   * ⚠️ THE DB VALUE IS UNTOUCHED AND MUST STAY UNTOUCHED. The key `priced` is
   * `portal.requests.status`, an enum created in portal migration 1 and read
   * by the projection's own stage map. This changes what a human READS, never
   * what the column holds — renaming the key would be a migration, which this
   * milestone is forbidden from taking.
   *
   * ⚠️ THIS MAP IS SHARED BY BOTH SURFACES — `MyRequests.tsx` (nurse) and
   * `Ops.tsx` (operator). That is deliberate, not incidental: stage 2 on
   * Helen's kiosk is ALREADY "Quoted", so moving the shared label removes an
   * operator-facing drift at the same time rather than creating a new one.
   * `Ops.tsx` renders the pill but NOT the spine, so there is no second
   * internal mismatch to introduce there. Measured at the ruling: exactly two
   * consumers, and no unit test asserts these strings — the live assertions
   * are `e2e/tests/portal/portal-smoke.spec.ts`, ops row and nurse row.
   *
   * ⛔⛔ SINCE T1749 M3 THERE IS NO SINGLE `statusLabels` MAP: it is
   * `nurseStatusLabels` + `operatorStatusLabels`, each with its own reader. The
   * paragraph below is the history that produced the split.
   * ⛔⛔ SINCE T1723 M7 THIS MAP HAD ONE CONSUMER, `Ops.tsx`, AND THE NURSE ROW
   * DID NOT READ IT. The sitting (Alex + Vicky, 2026-09-10) ruled Words 3: the
   * nurse never sees a quote stage on account-priced work, so her `priced` row
   * reads `Sent`, from `labelFor('request', status)` in `@ecmb/ui`, through the
   * shared request card. ⭐ AT T1749 M7 STEP 2 HELEN'S WORD BECAME `Priced`, not
   * `Quoted`: the operator half moved onto `labelFor('request', …, 'operator')`,
   * which is the kiosk's shipped vocabulary, so the two OPERATOR surfaces agree.
   * The divergence that remains is the RULED one, nurse against operator, and it
   * is the whole point of Words 3 (recorded in DESIGN.md). ⚠️ It stays under
   * `myRequestsCopy` because `Ops.tsx` imports it by this name. The live graders
   * are the ops-row `Priced` in `portal-smoke.spec.ts` and the nurse-row `Sent`
   * counts, plus the derivation arm in `components-portal.spec.tsx`.
   */
  /**
   * ⭐ T1749 M3 SPLIT THIS MAP IN TWO, and the split is the ruling made legible.
   * One map served two audiences who were already being told different things —
   * `myRequestsCopy.statusLabels` was read by the NURSE screen and by Helen's
   * `/ops` table at once — so Words 3's whole point (the nurse never sees a quote
   * stage on account-priced work) had to be enforced by a comment. It is now two
   * named maps, each with one reader, and the conformance registry parses BOTH.
   *
   * `nurseStatusLabels` is DERIVED FROM `labelFor('request', status)` and pinned
   * equal to it (`portal/test/client/components-portal.spec.tsx`), so it can
   * never drift from the shared source; it is written out as literals rather
   * than computed because the conformance registry in BOTH tools parses this
   * file as TEXT (the kiosk's vitest cannot resolve a portal module), and a
   * computed map would hand that parser nothing to read.
   * ⚠️ NOTHING IN `MyRequests.tsx` READS IT: the nurse screen calls `labelFor`
   * directly and `my-requests-when.spec.tsx` asserts it never reads a map from
   * here. This map exists so the WORDS are declared where the registry can see
   * them, which is what stops an unregistered word reaching a screen.
   */
  nurseStatusLabels: {
    new: 'Sent',
    priced: 'Sent',
    confirmed: 'Confirmed',
    declined: 'Declined',
    cancelled: 'Cancelled',
  } as Record<string, string>,
  /**
   * ⭐⭐ HELEN'S WORDS ON THE PORTAL'S OPS TABLE, AND M7 STEP 2 MOVED THEM ONTO
   * THE SHARED OPERATOR VOCABULARY: `New` / `Priced`, which is
   * `labelFor('request', status, 'operator')` and the kiosk's shipped
   * `STATUS_WORDS`. The M3 hold is over — it existed only so a live e2e pin was
   * not redded four milestones early, and this flip lands in the SAME commit as
   * the `portal-smoke.spec.ts` rewrite that grades the ops row.
   *
   * ⛔ THE TWO OPERATOR SURFACES NOW AGREE, which is the whole point: Helen
   * reads one word per state whether she is on the kiosk's Requests tab or the
   * portal's `/ops` table. ⚠️ AND THE NURSE STILL READS SOMETHING ELSE, by
   * design and not by drift — a `priced` row is `Sent` to her and `Priced` to
   * Helen (Words 3, R3). The divergence that is RULED is nurse-vs-operator; the
   * one that was a DEFECT was operator-vs-operator, and it is this map's flip
   * that ends it.
   * ⚠️ THE DB VALUE IS UNTOUCHED. `priced` is `portal.requests.status`, an enum
   * from portal migration 1; this changes what a human READS.
   */
  operatorStatusLabels: {
    new: 'New',
    priced: 'Priced',
    confirmed: 'Confirmed',
    declined: 'Declined',
    cancelled: 'Cancelled',
  } as Record<string, string>,
  loadError: 'Could not load requests.',
};

/**
 * ⭐ T1717 M3 — the "My residents" tab (P0-1/P0-2/P0-3/P0-4).
 *
 * ⛔ EVERY REFUSAL HERE NAMES THE CAPABILITY, NEVER A PERSON AND NEVER THE
 * PHONE (P1-1). A nurse who cannot add a resident is looking at a closed
 * `PORTAL_RESIDENT_WRITES_ACK_ARTICLE9` (plan DD1); ringing the office cannot
 * open it, because `POST /api/residents` is the only writer that exists and no
 * operator screen calls it. "Ring East Coast Minibus" is still the right
 * remedy elsewhere in this file - a date outside the horizon, a cancellation
 * the kiosk has to hear about - because a person really can act on those.
 *
 * ⛔ NO PRICE WORD ANYWHERE ON THIS SCREEN (Rule 1), and none of these strings
 * carries a figure of any kind. `my-residents.spec.tsx` sweeps the rendered
 * DOM for one, with a planted positive control.
 *
 * ⚠️ NEXT OF KIN IS RENDERED ONLY HERE AND ONLY TO THE NURSE OF THAT HOME. It
 * reaches no alert, no log, no Sentry envelope and no kiosk screen - see
 * `core/residents-repo.ts`'s `ResidentListItem` docblock for where that is
 * actually enforced.
 */
export const myResidentsCopy = {
  title: 'My residents',
  loadError: 'Could not load your residents.',
  emptyState: 'No residents yet. Add the first one here, or type a new name on a request.',
  /** ⛔ THE CLOSED VARIANT OF THE EMPTY STATE, because the open one PROMISES a
   *  form that is not on the screen. An empty list plus "add the first one
   *  here" beside no form is the same class of lie this milestone removes. */
  emptyStateClosed: 'No residents on your list yet.',
  /** The one line that replaces the add form and every row control when writes
   *  are closed. Deliberately the same sentence the request form's refusals
   *  use, so a nurse who meets it twice reads one fact, not two. */
  writesClosed: 'Adding or changing residents is not switched on here yet.',
  /**
   * ⛔ IT DOES NOT DOUBLE THE PREFIX (M3 review, ux). The live roster showed
   * rooms typed as "Room 12", which this rendered as "Room Room 12". A hint on
   * the field (`roomHint`) asks for the bare value from now on; this handles
   * what is ALREADY stored, which no hint can reach. Word-boundary anchored, so
   * a room genuinely called "Roomy" still gets its prefix.
   */
  roomLine: (room: string): string => {
    const value = room.trim();
    // ⚠️ `value`, NOT `room`, on BOTH branches — T1717 M7, Captain deltas #24.
    // The prefixed branch rendered the RAW string, so "  12" came out as
    // "Room   12" while the branch that decides used the trimmed one: the
    // function trimmed for its own test and then rendered something else.
    return /^room\b/i.test(value) ? value : `Room ${value}`;
  },
  // ⚠️ `nextOfKinLine` STOOD HERE and is deleted with the roster row that
  // rendered it (T1868 M6).
  /** ⛔ STATED, NOT OMITTED. A row with the pair simply missing looks complete;
   *  saying it is missing is what gets it filled in, and DD3 makes the pair
   *  optional precisely so a new arrival can be added before it is known. */
  // ⚠️ `noNextOfKin` STOOD HERE (T1868 M6). It said "No next of kin yet." on a
  // roster row that had none, on the reasoning that naming the absence is what
  // gets it filled in. There is no next of kin to be missing any more, so the
  // sentence would name a field the nurse cannot supply.
  editButton: 'Edit',
  saveButton: 'Save',
  cancelEditButton: 'Cancel',
  removeButton: 'Remove from list',
  removeConfirmPrompt: (name: string): string => `Remove ${name}?`,
  /** ⛔ THE SCOPE NOTE IS THE TRUE HALF OF "REMOVE". `core/roles.sql` withholds
   *  DELETE, so the row survives and every past request still names them; the
   *  button stops offering them on new requests and nothing else. */
  removeScopeNote: 'They stay on past requests.',
  removeConfirmYes: 'Yes, remove',
  removeConfirmKeep: 'Keep them',
  addTitle: 'Add a resident',
  addButton: 'Add to the list',
  /** ⛔ LABELS CHOSEN AGAINST THE LIVE e2e VOCABULARY (DD11). The portal lane
   *  matches labels by case-insensitive SUBSTRING, so a field called "Resident
   *  name" would make `getByLabel('Resident')` ambiguous the moment a spec
   *  visits this tab and blank a whole serial lane. "Their name" / "Their room"
   *  contain no needle; "Next of kin phone" is the EXACT existing needle used
   *  for the same purpose, which is the one permitted form of reuse. */
  nameLabel: 'Their name',
  roomLabel: 'Their room',
  roomHint: 'Just the number, e.g. 12',
  // ⚠️ THE THREE NEXT-OF-KIN LABELS STOOD HERE (T1868 M6). ⛔ THE NEEDLE RULE
  // ABOVE OUTLIVES THEM AND IS THE REASON THIS NOTE EXISTS: "Next of kin phone"
  // was the one permitted reuse of an EXACT existing e2e needle, so a future
  // label on this tab must be re-checked against the derived vocabulary rather
  // than assumed free — the freed needle is not a licence, it is one fewer
  // collision to reason about.
  alreadyOnList: (name: string): string => `${name} is already on your list.`,
  listFull: 'Your list is full. Remove someone who has left.',
  /** ⭐ DD10 — one person is one row. A name that was removed comes BACK rather
   *  than being minted again, which is what stops a second copy of somebody's
   *  next-of-kin details existing under a second id. */
  removedOffer: (name: string): string => `${name} was removed from your list. Put them back?`,
  putBackButton: 'Put them back',
  goneFromList: 'That resident is not on your list any more.',
  /**
   * ⭐⭐ THE OUTCOME LINE — T1717 M7, ux-review WARN on the live drive.
   *
   * ⛔ WHAT THE GAP WAS. After Save (add or edit) and after a confirmed Remove
   * the screen simply re-rendered: no sentence said the write had happened, and
   * focus dropped to `<body>`. A sighted nurse inferred the outcome from the
   * list changing; a screen-reader nurse was told NOTHING AT ALL, and everyone
   * lost their place in the tab order. Refusals had a voice on this screen from
   * M3; successes did not.
   * ⚠️ EACH ONE NAMES THE RESIDENT, because "Saved." on a screen listing a whole
   * home does not say WHO was saved — and on the remove path, which is the
   * destructive one, that is the sentence that catches the wrong tap.
   * ⚠️ Past tense and no exclamation: it reports, it does not congratulate.
   */
  addedNotice: (name: string): string => `${name} added to your list.`,
  savedNotice: (name: string): string => `${name} saved.`,
  removedNotice: (name: string): string => `${name} removed from your list.`,
  /** ⭐ T1717 rework: the put-back was the one write with no outcome line
   *  (Captain's live drive). Same voice as the three above. */
  putBackNotice: (name: string): string => `${name} put back on your list.`,
};

export const opsCopy = {
  title: 'Ops inbox',
  passcodeLabel: 'Ops passcode',
  passcodeSubmit: 'Continue',
  passcodeWrong: 'That passcode is not right.',
  passcodeDisabled: 'Ops access is not turned on for this environment.',
  tableEmpty: 'No requests yet.',
  /** ⭐ T1683 M3 (A1). First column: it is what a phone call opens with. */
  columnReference: 'Ref',
  columnHome: 'Home',
  columnNurse: 'Nurse',
  columnResident: 'Resident',
  /**
   * ⚠️ THIS DOCBLOCK USED TO INTRODUCE THE NEXT-OF-KIN COLUMN (T1717 M4, DD9),
   * which /ops was the ONLY operator screen to show. T1868 M6 removed it, and
   * with it the last render of the pair anywhere in this repo.
   * ⛔ DD9's RULE IS UNCHANGED AND STILL BINDING for whatever comes next: next
   * of kin never reached a kiosk screen, the kiosk database or a kiosk-served
   * response — the kiosk RECEIVED the pair in the ops payload and discarded it
   * via `PORTAL_REQUEST_KEEP_KEYS`. Now nothing sends it. ⛔ Do not widen that
   * keep-set on the reasoning that the pair is gone; the keep-set is an
   * allowlist, and its job is the keys nobody has thought of yet.
   * ⛔ WHAT REPLACED IT IS NOT ON THIS ROW. Helen's number to ring is the HOME's
   * `fallback_phone`, resolved home-side on the kiosk's own PortalRequests
   * screen — one organisation-level value, not a third party per journey.
   *
   * ⚠️ ROOM IS BESIDE THE RESIDENT, not beside the destination, because it is a
   * fact about the person the driver is collecting, and Helen reads the row
   * left-to-right as "who, where from, where to".
   */
  columnRoom: 'Room',
  /**
   * ⭐ T1730 (Alex, 2026-09-03, q731; ADR2-D15) - THE ONE BIT FROM THE NURSE'S
   * EQUIPMENT TICK, beside Room because it is also a fact about the person the
   * driver is collecting. "Wheelchair" is the nurse's own word
   * (`EQUIPMENT_OPTIONS[0]`); the kiosk renders the same fact as "Accessible
   * vehicle", its registry word. ⛔ The cell says "Yes" or nothing - never the
   * equipment list, never a placeholder.
   */
  columnWheelchair: 'Wheelchair',
  wheelchairYes: 'Yes',
  // ⚠️ `columnNextOfKin` STOOD HERE (T1868 M6): the /ops table's next-of-kin
  // column is gone, header and cell together.
  columnDestination: 'Destination',
  columnPayer: 'Payer',
  columnWhen: 'When',
  // ⭐ board 2188 (Alex, 2026-09-18) — the derived collection's word on Helen's
  // table, ruled by Alex because this renderer has no `when.pickup` slot to fill.
  // `Collection` is the register's own (`format-when.ts` MEANING_WORDS), and it
  // matches the nurse's FORM.
  // ⛔ IT DOES NOT MATCH THE TWO CARDS, and an earlier version of this comment
  // claimed it did (plan-alignment, board 2188 rework 2). They render a FIXED
  // `<dt>Pickup</dt>` from `packages/ui/src/Card.tsx:472`, which no caller
  // supplies. Four surfaces, two words, for one fact — see the fuller note at
  // `collectionDerivedLabel`. A sitting item on R-2073-c, not a worker's to take.
  // ⚠️ The T632 no-"time" rule does NOT bind here: that rule protects the
  // appointment fieldset's `getByLabel('Time')`, and this is an operator table
  // with no time input. The word is still bare, for consistency with the form.
  collectionLine: 'Collection',
  columnStatus: 'Status',
  columnPrice: 'Price',
  columnActions: 'Actions',
  // ⚠️ `nextOfKinCell` STOOD HERE (T1868 M6) and was this file's canonical
  // "filter before you join" example: it composed a name and a number with " · "
  // and dropped the separator when either half was absent, because a separator
  // over nothing reads as a DROPPED field rather than an absent one. The rule is
  // unchanged and is cited from `RequestForm.tsx`'s picker label, which is now
  // where it lives.
  actionPrice: 'Price',
  actionReprice: 'Update price',
  // ⚠️ Board 2814 M1 - `actionConfirm` stood here; `/ops` no longer confirms (plan DD-3).
  actionDecline: 'Decline',
  actionCancel: 'Cancel',
  /**
   * ⭐ T1011 M6 — the same disclosure as the kiosk's request inbox, on the screen
   * `PortalRequests.tsx`'s header says Helen "used to" use. It was never removed
   * and is still fully routed (app.tsx `route === 'ops'`), so leaving it silent
   * would have left one live surface still lying while its replacement told the
   * truth.
   *
   * ⚠️ WORDED FOR THE OFFICE, not for a nurse: it names the calculator, because
   * the person reading this has one.
   */
  cancelScopeNote:
    'Cancelling a request here only ends the request. If the run is already in the '
    + 'calculator it is still going ahead, and still on the bill: cancel the run there as well.',
  priceInputLabel: 'Price (EUR)',
  // Whole-euro examples only (round-3, P6): decimals are still accepted
  // silently by parseEuroToCents, the copy just stops advertising them.
  priceInputPlaceholder: '120',
  priceInvalidError: 'Enter the price in euro, like 120.',
  waitingNotesLabel: 'Note for the home (optional)',
  illegalTransitionError: 'That change is not allowed from the current status.',
  // Quarterly access review (2026-07-21): quarterly to each home's
  // administrator, monthly to Alex.
  accessReviewOpen: 'Access review',
  accessReviewBack: 'Back to requests',
  accessReviewTitle: 'Who should still have access?',
  accessReviewBlurb:
    'Send this to the home once a quarter. They fill in the last column and send it back. Nobody has to remember who left. Marking Keep or Remove builds the review to send; it does not change access here.',
  accessReviewCopyCsv: 'Copy as a table',
  accessReviewDownload: 'Download for Excel',
  accessReviewCopied: 'Copied. Paste it into an email or a spreadsheet.',
  accessReviewColAccess: 'Can sign in',
  accessReviewAccessYes: 'Yes',
  accessReviewAccessNo: 'No - access removed',
  accessReviewKeep: 'Keep',
  accessReviewRemove: 'Remove',
  accessReviewNever: 'never signed in',
  accessReviewDormantFor: (days: number): string => `no activity in ${String(days)} days`,
  accessReviewColNurse: 'Nurse',
  accessReviewColEmail: 'Work email',
  accessReviewColLastUsed: 'Last used',
  accessReviewColDevices: 'Devices',
  accessReviewColFlag: 'Flag',
  accessReviewColDecision: 'Still needs access?',
};

export const devMailboxCopy = {
  title: 'Dev mailbox',
  demoLabel: 'Testing only. Shows sign-in code emails; not part of the real product.',
  filterLabel: 'Filter by recipient email',
  filterPlaceholder: 'nurse@home.example',
  notAvailable: 'Not available.',
  emptyState: 'No mail yet.',
  // The screen polls, but a visible control matters: it tells the reader the
  // box is meant to keep updating, rather than leaving them to wonder
  // whether an empty list is stale.
  refreshButton: 'Refresh',
  autoRefreshNote: 'Updates on its own every few seconds.',
  // Live countdown under each mail; the only expiry statement (the email
  // body deliberately names none, round-3 P1).
  codeExpiresIn: (minutes: number): string =>
    minutes === 1 ? 'Code expires in under a minute.' : `Code expires in about ${String(minutes)} minutes.`,
  codeExpired: 'Code expired. Request a new one.',
};

/**
 * ⭐ T2075 M4 (plan Step 2) — THE HOME-WIDE REQUESTS TAB, reception's and the
 * admin's. ⛔ APPENDED AFTER `devMailboxCopy` ON PURPOSE: `check-copy.sh` keys
 * four exemptions into this file BY LINE, and everything above them moves them;
 * a new object at the END moves nothing (the two `shellCopy` tab labels above
 * did, and were re-keyed). The words here follow the nurse vocabulary: `Sent`,
 * never `New` or `Priced` (Words 3), and `repeating`, never the retired words.
 */
export const homeRequestsCopy = {
  /** ⭐ T2173: was 'Requests'. Reads ambiguously beside `shellCopy.tabMyRequests`
   *  on reception's and admin's strip, so the tab and this heading were renamed
   *  together — they are the same destination and must say the same word. Nurses
   *  never see either (the strip gates the tab on role). */
  title: 'All requests',
  /** The cap is the wire's, stated here so an older past row that is not
   *  listed is explained rather than hidden (plan M4 success criteria); the
   *  figure arrives as an argument so it is written once, in `HOME_REQUESTS_CAP`. */
  capHint: (cap: number): string => `Every request for your home. The newest ${String(cap)} are listed.`,
  showLegend: 'Show',
  showUpcoming: 'Upcoming',
  showPast: 'Past',
  groupLegend: 'Group',
  /** ⭐ T2075 M5 (closing the M4 ux-review) — the leading chip of the group
   *  filter, pressed when no group is chosen, so the filter can be cleared by
   *  a tap and not only by un-pressing the chosen chip. */
  homeRequestsAllGroups: 'All',
  viewLegend: 'View',
  viewByDate: 'By date',
  viewByStaff: 'By staff',
  staffLabel: 'Staff member',
  staffAny: 'Anyone',
  statusLabel: 'Status',
  statusAny: 'Any status',
  /** The heading a By-staff group takes when its rows name nobody (pre-v13
   *  rows, or a staff member since removed). */
  byStaffUnnamed: 'No name given',
  /** The By-staff heading: "Grainne Ni Bhriain, 3 requests". */
  byStaffHeading: (name: string, n: number): string =>
    `${name}, ${n === 1 ? '1 request' : `${String(n)} requests`}`,
  shown: (n: number): string => (n === 1 ? '1 request shown.' : `${String(n)} requests shown.`),
  /** The room beside the resident on a row: "Nora Byrne, Room 12". */
  subject: (resident: string, room: string): string => (room.trim() === '' ? resident : `${resident}, ${room}`),
  jobRefLabel: (ref: string): string => `Ref ${ref}`,
  returnTripLabel: 'and back',
  /** The gated empty state (plan Step 2, verbatim): the R-9 flag is OFF, so
   *  the account sees only what it entered itself. Neutral, never the ok tint. */
  homeRequestsGatedNotice: 'Requests from other groups will appear here once East Coast Minibus switches it on for your home.',
  /** The gated zero-rows headline: NEUTRAL, because the persistent `Notice`
   *  above it already carries the gated sentence (the M5 ux-review found the
   *  same sentence rendered twice on an empty account). */
  homeRequestsEmptyGated: 'No requests yet.',
  homeRequestsEmpty: 'No requests match.',
  loadError: 'Could not load requests.',
};

/**
 * ⭐ T2075 M5 (plan Step 5) — THE ADMIN'S STAFF TAB: the roster (Staff members)
 * and the sign-in accounts. ⛔ APPENDED AT THE END ON PURPOSE, after
 * `homeRequestsCopy`: `check-copy.sh` keys four exemptions into this file BY
 * LINE and everything above them moves them; a new object at the END moves
 * nothing. ⛔ THE LABELS ARE DISTINCT ACROSS THE WHOLE VIEW so `getByLabel`
 * never hits strict-mode ambiguity: the members add row's group field is
 * **Group**, the edit-in-place row's is **Change group**, the accounts add
 * row's is **Sign-in group**; the type select is **Sign-in type**. No banned
 * word, no spaced hyphen, no dash.
 */
export const staffCopy = {
  title: 'Staff',
  sectionLegend: 'Which part of staff to show',
  sectionMembers: 'Staff members',
  sectionAccounts: 'Sign-in accounts',
  loadError: 'Could not load the staff list.',
  saveError: 'Could not save that. Try again.',
  // --- Staff members ---
  membersIntro: 'The people a request can be asked for by. Add them one at a time or import a spreadsheet.',
  addNameLabel: 'Name',
  addGroupLabel: 'Group',
  addGroupHint: 'Type a group or pick one the home already has.',
  addButton: 'Add',
  /** ⭐ T2173 — was 'Import CSV'. It is the file control's LABEL, so it is also
   *  its accessible name and the words on the native button; 'Import CSV' named
   *  the outcome, not the action, and Alex read the bare control as a draft.
   *  ⚠️ CHANGING THIS MOVES THE NET: `e2e/tests/portal/portal-staff.spec.ts`
   *  drives the control with `getByLabel(...).setInputFiles` and the DESIGN.md
   *  locked-names block records it, so both move in the same change. */
  importLabel: 'Choose a spreadsheet',
  /** ⭐ T2173 — 'Other columns are ignored.' was LITERAL, and Alex round-tripped
   *  an export with Active edited expecting it to stick. Active is now read; the
   *  sentence says so, and still says the rest are ignored (S13/A4 stand). */
  importHint: 'A file with a Name column and a Group column. An Active column of yes or no is read too. Other columns are ignored.',
  /** ⭐ T2173 — the chosen file's name, appended to the hint line rather than
   *  rendered beside the control (which added a grid track and clipped the native
   *  widget). The leading space is deliberate: it joins the hint sentence. */
  chosenFilePrefix: ' Chosen: ',
  /** ⭐⭐ T2173 rework 1 (security Medium) — THE ARMED CONFIRM ON A BULK
   *  DEACTIVATION. A single deactivation has always been two-step armed, while
   *  an import could set up to `STAFF_IMPORT_MAX_ROWS` names inactive on a file
   *  chooser's change event with no confirm at all — and the requester picker
   *  lists ACTIVE names only, so one stale export could empty every nurse's
   *  requester list. The bulk path inherited the per-row path's authority
   *  without its ceremony. It states the COUNT, because the count is the thing
   *  the admin cannot see in a spreadsheet she exported weeks ago. */
  importArmed: (n: number): string =>
    n === 1
      ? 'This spreadsheet will deactivate 1 name.'
      : `This spreadsheet will deactivate ${String(n)} names.`,
  confirmImport: 'Import',
  /** ⛔ NOT "Cancel": the choice is between two outcomes, not between doing
   *  something and doing nothing, and "Keep" names what the other button
   *  protects — the names stay as they are. */
  keepImport: 'Keep',
  exportButton: 'Export CSV',
  exportFileName: (date: string): string => `staff-${date}.csv`,
  membersTableLabel: 'Staff members',
  /** The scroll region's name says WHAT scrolls and WHICH WAY (`TableScroll`). */
  membersScrollLabel: 'Staff members, scrolls sideways',
  colName: 'Name',
  colGroup: 'Group',
  colLastRequested: 'Last requested',
  colActions: 'Actions',
  never: 'Never',
  membersEmpty: 'No staff yet. Add a name above or import a spreadsheet.',
  membersLoading: 'Loading staff.',
  edit: 'Edit',
  editNameLabel: 'Change name',
  editGroupLabel: 'Change group',
  save: 'Save',
  cancel: 'Cancel',
  deactivate: 'Deactivate',
  deactivateArmed: (name: string): string => `Remove ${name} from the list? Their past requests keep their name.`,
  confirmDeactivate: 'Yes, remove',
  reactivate: 'Reactivate',
  inactive: 'Removed',
  editNameFor: (name: string): string => `Edit ${name}`,
  memberAdded: (name: string): string => `${name} added.`,
  memberSaved: (name: string): string => `${name} saved.`,
  memberRemoved: (name: string): string => `${name} removed from the list.`,
  memberRestored: (name: string): string => `${name} is back on the list.`,
  deactivateFor: (name: string): string => `Deactivate ${name}`,
  reactivateFor: (name: string): string => `Reactivate ${name}`,
  // --- the import report ---
  reportTitle: 'Import finished.',
  reportLine: (added: number, skipped: number, rejected: number): string =>
    `${String(added)} added, ${String(skipped)} already on the list, ${String(rejected)} not added.`,
  reportIgnored: (columns: string[]): string => `Ignored columns: ${columns.join(', ')}.`,
  reportEncoding: (encoding: string): string => `Read as ${encoding}.`,
  reportNewGroups: (groups: string[]): string => `New groups created: ${groups.join(', ')}. Check for a misspelt group.`,
  reportAlsoInAnotherGroup: (n: number): string =>
    n === 1 ? '1 name is now in two groups.' : `${String(n)} names are now in two groups.`,
  /** ⭐ T2173 — what the Active column CHANGED, so a round-tripped export says so
   *  out loud. ⛔ A SEPARATE, CONDITIONAL CLAUSE rather than two more arguments to
   *  `reportLine`, for two reasons: it follows the pattern its two neighbours
   *  already use (appended only when non-zero, so an ordinary import reads no
   *  longer than before), and it keeps `reportLine`'s three-argument shape, which
   *  three matchers read as a prefix — `portal-staff.spec.ts` at the import
   *  notice and the all-skipped assertion, and `staff.spec.tsx`'s report arm.
   *  ⚠️ APPENDED AT THE TAIL for the same reason: those matchers are unanchored
   *  and ordered, so text inserted before or between them reds the lane. */
  reportStateChanges: (deactivated: number, reactivated: number): string => {
    const parts: string[] = [];
    if (deactivated > 0) parts.push(`${String(deactivated)} deactivated`);
    if (reactivated > 0) parts.push(`${String(reactivated)} reactivated`);
    return parts.length === 0 ? '' : `${parts.join(', ')}.`;
  },
  rejectedTableLabel: 'Rows not added',
  colLine: 'Line',
  colReason: 'Reason',
  reasons: {
    name_required: 'No name',
    name_too_long: 'Name too long',
    name_formula: 'Name starts with a formula character',
    group_required: 'No group',
    group_too_long: 'Group too long',
    group_formula: 'Group starts with a formula character',
    // ⭐ T2173 — the key for `StaffCellRefusal`'s new member. ⛔ REQUIRED, not
    // optional: this map is typed `as Record<string, string>`, so a missing key
    // makes the rejected-rows table print the raw token `active_not_yes_no` to
    // the admin. It names the two words that work, because the refusal's whole
    // job is to tell her what to type instead.
    active_not_yes_no: 'Active must be yes or no',
  } as Record<string, string>,
  refusals: {
    empty: 'That file is empty.',
    no_name_column: 'That file has no Name column.',
    no_group_column: 'That file has no Group column.',
    too_many_rows: (rows: number, max: number): string =>
      `That file has ${String(rows)} rows. The most that can be imported at once is ${String(max)}.`,
  },
  // --- Sign-in accounts ---
  accountsIntro: 'The sign-ins for your home. A group sign-in sees its own group. Reception sees the whole home.',
  accountsTableLabel: 'Sign-in accounts',
  accountsScrollLabel: 'Sign-in accounts, scrolls sideways',
  colMailbox: 'Mailbox',
  colType: 'Type',
  colAccountGroup: 'Group',
  colActive: 'Active',
  colPin: 'PIN set',
  yes: 'Yes',
  no: 'No',
  types: { group: 'Group', reception: 'Reception', admin: 'Administrator' } as Record<string, string>,
  addMailboxLabel: 'Mailbox',
  addTypeLabel: 'Sign-in type',
  addAccountGroupLabel: 'Sign-in group',
  addAccountButton: 'Add sign-in',
  addAccountNoGroups: 'Add a staff member first so there is a group to choose.',
  accountAdded: (email: string): string => `${email} can now sign in with an email code.`,
  accountEmailTaken: 'That address is already on a list.',
  accountAdminNotAllowed: 'Only East Coast Minibus can add an administrator.',
  accountCreateCapped: 'Too many sign-ins added in the last hour. Try again later.',
  deactivateAccount: 'Deactivate',
  deactivateAccountArmed: (email: string): string => `Stop ${email} signing in? They are signed out everywhere.`,
  confirmDeactivateAccount: 'Yes, deactivate',
  reactivateAccount: 'Reactivate',
  resetPin: 'Reset PIN',
  /** ⭐ T2173 (board 2098 Medium 4 + Alex's item 5). TWO changes, both deliberate.
   *  ⛔ IT NAMES THE MAILBOX, not the group label or the display name: the table
   *  this confirm sits above lists accounts BY MAILBOX, so naming anything else
   *  made the admin match a sentence against a column that was not there.
   *  ⛔ IT SAYS WHAT THE NURSE WILL SEE, because the PIN pad cannot tell her. The
   *  pad answers every failure identically (board 786/1049, `Picker.tsx` docblock),
   *  so the person who knows a reset happened is the ADMIN, and this is the only
   *  place that knowledge can be turned into something the nurse can act on. */
  resetPinArmed: (email: string): string =>
    `Everyone signed in as ${email} will be signed out. Their old PIN will stop working without saying why, `
    + 'so tell them to use Sign in with email on the device and set a new one.',
  confirmResetPin: 'Yes, reset the PIN',
  pinReset: (email: string): string => `PIN reset for ${email}. Their next sign-in asks for a code.`,
  accountDeactivated: (email: string): string => `${email} deactivated.`,
  accountReactivated: (email: string): string => `${email} can sign in again.`,
  accountsEmpty: 'No sign-ins yet.',
  deactivateAccountFor: (email: string): string => `Deactivate ${email}`,
  reactivateAccountFor: (email: string): string => `Reactivate ${email}`,
  resetPinFor: (email: string): string => `Reset PIN for ${email}`,
  // --- the review ---
  downloadReview: 'Download access review',
  reviewFileName: (date: string): string => `access-review-${date}.csv`,
};

/**
 * ⛔ T2075 M6 (code-review round 2, finding 1) — THE FAILED-LOCK SCREEN, a
 * DISTINCT fail-closed screen whose only affordance re-attempts the LOCK. The
 * generic load-error screen's "Try again" reloads the auth state, and after
 * a lock POST that failed the server had NOT revoked the session, so that
 * button put the resident list straight back on an unattended shared PC
 * (live-reproduced). These two sentences say what is happening and offer
 * the one safe act. ⛔ APPENDED AT THE END, not in `shellCopy`: `check-copy.sh`
 * keys four exemptions into this file BY LINE and `shellCopy` sits above all
 * four, so a line added there moves every key; a new object at the end moves
 * nothing (the `homeRequestsCopy` rule). No banned word, no spaced hyphen.
 */
export const lockFailedCopy = {
  notice: 'Locking this screen did not work. Trying again.',
  retry: 'Try to lock again',
};

/**
 * ⭐ board 2707 (M4, plan DD5, R-5) — SEEN ON THE CARD: the sentence a request
 * East Coast Minibus has opened carries on the nurse's My requests and on
 * reception's All requests. Read by `MyRequests.tsx`'s `seenFor`, the one place
 * both screens derive Seen from. ⛔ board 2732 (Alex, 2026-10-02) removed the
 * `Seen` time row and its `seenBy` string: the card says WHETHER, never WHEN.
 * ⛔ APPENDED AT THE END ON PURPOSE (the `homeRequestsCopy` rule): `check-copy.sh`
 * keys its exemptions for this file BY LINE (re-derive how many with
 * `/usr/bin/grep -c "'../portal/src/client/copy.ts', [0-9]" quote-helper/scripts/check-copy.sh`;
 * older notes above say four), and a new object at the end moves none of them. `myRequestsCopy.waitingOn` is untouched: it stays keyed by status
 * alone, and the screens pick this sentence instead when the request is seen and
 * not yet confirmed (`new` or `priced`).
 * ⛔ NO MONEY WORD, no banned word, no dash: the portal copy census and
 * `check:copy` both read this object. ⚠️ R-9: the door is named, never a person;
 * who at the office opened it is not on the wire (plan NOT shipped #2). The
 * wording is the plan's proposal, flagged for the next Vicky sitting.
 */
export const requestSeenCopy = {
  /** The waiting-on sentence the pill leads once the office has seen it. */
  waitingOnSeen: 'East Coast Minibus has seen it and will confirm it.',
};

/**
 * ⭐⭐ board 2732 M5 (plan DD3, DD4, R-4, R-5, R-8) — THE REQUESTER BOX: the
 * request form's "Who is requesting?" became the resident box's suggestion
 * pattern, with an explicit `Add` for a missing name. Every sentence here is
 * new and flagged for the next Vicky sitting.
 * ⛔ APPENDED AT THE END ON PURPOSE (the `homeRequestsCopy` rule): `check-copy.sh`
 * keys its exemptions for this file BY LINE, and a new object at the end moves
 * none of them. ⛔ NO MONEY WORD, no banned word, no dash, and never the
 * resident region's own words for a count (three request-form arms count the
 * status regions holding them). Never "ask your administrator" to an admin.
 */
export const requesterBoxCopy = {
  /** The visible and announced count of names under the box. */
  found: (n: number): string => (n === 1 ? '1 name found. Tap it, or keep typing.' : `${String(n)} names found. Tap one, or keep typing.`),
  foundCapped: (shown: number, total: number): string => `${String(shown)} of ${String(total)} names shown. Tap one, or keep typing.`,
  noneFound: 'No name on the list yet.',
  /** The explicit act that stages a new name; written at Send, never on a tap. */
  addChip: (name: string): string => `Add "${name}" as a new name`,
  /** The staged hint, naming the group before anything is written. */
  addHint: (name: string, group: string): string => `${name} will be added to ${group} when you send.`,
  addHintNoGroup: (name: string): string => `${name} will be added to your group when you send.`,
  /** Empty roster, per role (R-8). Neutral, never a success tone. */
  emptyWardNoAdd: "No names in your group yet. Ask your home's admin to add the staff list.",
  emptyReception: "No names on your home's list yet. Your home's admin adds them on the Staff tab.",
  emptyAdmin: "No names on your home's list yet. Add them on the Staff tab.",
  /** Refusals from `POST /api/staff`, shown under the box; nothing else is written. */
  deactivated: "That name was taken off your home's list. Choose another name, or ask your home's admin.",
  deactivatedAdmin: "That name was taken off your home's list. Choose another name, or reactivate it on the Staff tab.",
  ceiling: "Your home's list is full. Choose a name from the list, or ask your home's admin.",
  ceilingAdmin: "Your home's list is full. Choose a name from the list, or ring East Coast Minibus.",
  throttled: 'Too many names were added just now. Choose a name from the list, or ring East Coast Minibus.',
  groupRefused: 'That name cannot be added to this group. Choose a name from the list.',
  groupUnset: "Your sign-in has no group yet. Ask your home's admin.",
  nameRefused: 'That name cannot be added as it is typed. Check it, or choose a name from the list.',
  /** The box's hint while reception / admin have not chosen a group (the box is disabled). */
  chooseGroupFirst: 'Choose a group first, then type a name.',
};

/**
 * ⭐⭐ Board 2788 M5 (plan DD-8, DD-10, DD-11, DD-14, DD-19; R-2 to R-5) - THE
 * HOME'S CHANGE, ITS TWO ASKS, AND WHAT A CARD SAYS ABOUT THEM, on My requests
 * and All requests (`request-actions.tsx`). Every sentence here is new and is
 * flagged for the next Vicky sitting.
 * ⛔ APPENDED AT THE END ON PURPOSE (the `homeRequestsCopy` rule): `check-copy.sh`
 * keys its exemptions for this file BY LINE, and a new object at the end moves
 * none of them.
 * ⛔ NO STATUS WORD IN THE LINES (`Sent`, `Seen`, `Confirmed`): the pill and the
 * step line own those words and `my-requests-structure.spec.tsx` counts them.
 * No money word, no banned word, no dash. Where a door is shut the sentence
 * ends on the phone, which never closes.
 * ⛔ THE PHONE NUMBER IS NOT HERE. It lives once, in `ECMB_PHONE`
 * (`shared/request-schema.ts`), the source the server's refusal sentence reads
 * too, and `office-phone-parity.spec.ts` pins it against the site. This module
 * keeps its one type-only import, so the card renders the number beside
 * `ringLead` rather than this object importing it.
 * ⚠️ THE TIME WORDS FOLLOW `time_meaning` (DD-11): `collection` is the word the
 * request form already uses for the derived pickup (`collectionDerivedLabel`).
 */
export const requestChangeCopy = {
  /** The controls. A New or Priced card changes; a Confirmed card asks. */
  changeButton: 'Change date and time',
  askChangeButton: 'Ask for a change',
  askCancelButton: 'Ask to cancel',
  /** DD-10: a Confirmed repeating trip is asked to stop, never stopped here. */
  askStopButton: 'Ask to stop this repeating trip',
  /** The inline form, the same two controls for a change and a change ask. */
  changePrompt: 'Choose the new date and time, then save it.',
  askChangePrompt: 'Choose the date and time you would like. East Coast Minibus will tell you if it can be done.',
  dayLabel: 'New date',
  /** Keyed by `time_meaning`; `other` is a legacy row that recorded none. */
  timeLabels: {
    appointment: 'New appointment time',
    collection: 'New collection time',
    other: 'New time',
  } as Record<'appointment' | 'collection' | 'other', string>,
  changeSave: 'Save the change',
  askChangeSave: 'Send the ask',
  notNow: 'Not now',
  /** When the server sends no sentence of its own. */
  failed: 'That did not go through. Look at the request again, or ring us.',
  /** The route saved it but could not email the office (`alertFailed`). */
  alertFailed: 'Saved, but we could not get word to the office. Ring East Coast Minibus so they know.',
  /** The ask to cancel: two taps, like Cancel. */
  askCancelPrompt: 'Ask East Coast Minibus to cancel this trip?',
  askStopPrompt: 'Ask East Coast Minibus to stop this repeating trip?',
  askCancelNote: 'The trip still runs until East Coast Minibus says yes. If it is soon, ring us.',
  askCancelYes: 'Yes, ask to cancel',
  askStopYes: 'Yes, ask to stop it',
  /** The highlighted line: a neutral chip carrying the lead, then the rest. */
  lead: {
    changed: 'Changed',
    changeAsked: 'Change asked',
    cancelAsked: 'Cancel asked',
    stopAsked: 'Stop asked',
    notChanged: 'Not changed',
    notCancelled: 'Not cancelled',
    notStopped: 'Not stopped',
  },
  waiting: 'waiting for East Coast Minibus',
  /** "collection 08:15 to 09:45, waiting for East Coast Minibus". */
  askedRest: (span: string): string => `${span}, waiting for East Coast Minibus`,
  /** "collection now 09:45". */
  nowRest: (what: string, clock: string): string => `${what} now ${clock}`,
  /** The bracket an appointment carries for its collection (DD-11). */
  collectionBracket: (span: string): string => `(collection ${span})`,
  span: (from: string, to: string): string => `${from} to ${to}`,
  timeWords: {
    appointment: 'appointment',
    collection: 'collection',
    other: 'time',
  } as Record<'appointment' | 'collection' | 'other', string>,
  /** DD-5's closed list of Helen's reasons, worded for the home. */
  declineReasons: {
    no_bus: 'we have no bus free at that time',
    too_close: 'it is too close to the trip to change it here',
    please_ring: 'please ring us so we can sort it out',
  } as Record<string, string>,
  declineFallback: 'please ring us',
  /** DD-19: inside the cut-off the card offers no change, no ask and no
   *  Cancel; this line leads, and the number follows it as a `tel:` link. */
  ringLead: (hours: number): string =>
    `This trip is less than ${String(hours)} hours away. To change or cancel it, ring East Coast Minibus on `,
  /** ⭐ Board 2788 M6 (step 0b): the phone control's NAME, which says who it rings
   *  and carries the number it shows (label in name). */
  ringAria: (display: string): string => `Ring East Coast Minibus on ${display}`,
  /** The history, newest first, each line labelled by `time_meaning`. */
  historySummary: 'History',
  historyTimeWords: {
    appointment: 'Appointment',
    collection: 'Collection',
    other: 'Time',
  } as Record<'appointment' | 'collection' | 'other', string>,
  historyChanged: (what: string, span: string, by: string, at: string): string => `${what} ${span}, changed by ${by}, ${at}`,
  historyAsked: (what: string, span: string, by: string, at: string): string => `${what} ${span}, asked by ${by}, ${at}`,
  historyCancelAsked: (by: string, at: string): string => `Cancel asked by ${by}, ${at}`,
  historyStopAsked: (by: string, at: string): string => `Stop asked by ${by}, ${at}`,
  historyNotAgreed: (by: string, at: string): string => `Not agreed by ${by}, ${at}`,
  historyCancelled: (by: string, at: string): string => `Cancelled by ${by}, ${at}`,
  historyTurnedDown: (by: string, at: string): string => `Turned down by ${by}, ${at}`,
};
